// Price/markup helpers for liveflight offers. Pure reads + transforms
// against the canonical FlightOffer shape; markup math is the same
// regardless of who produced the offer.

import Vue from "vue";
import { number, sum, multiply, divide, round, subtract } from "mathjs";
import { deep_clone } from "@/utilities/";
import { log } from "@/utilities/commons";
import { track_form_event } from "@/utilities/integrations/rybbit";
import { as_number } from "@liveflight/providers/base/helpers";
import { slim_flight_offer } from "@liveflight/utilities/rybbit";

const decimals = 2;

/**
 * Apply the offer's markup percentage to a raw amount.
 * @param {object} flight_offer
 * @param {number|string} n Raw amount before markup
 * @param {string} [forced_mu] Forced markup percentage override
 * @returns {string} Marked-up amount, rounded to 2 decimals
 */
export function apply_markup_to_number(flight_offer, n, forced_mu) {
  const mu_percent = get_markup(flight_offer, forced_mu).mu_percent;
  return round(sum(n, multiply(n, mu_percent)), decimals).toString();
}

/**
 * Normalize a configured tier into a numeric band. Both bounds are optional:
 * an absent (or unparsable) `min` reads as "from zero", an absent `max` leaves
 * the band open-ended — the legacy min-threshold tier shape.
 * @param {{min?: number|string, max?: number|string, p?: number|string}} tier
 * @returns {{min: number, max: number, p: number}}
 */
const to_band = (tier) => {
  const { min, max, p } = tier || {};
  return {
    min: as_number(min),
    max: as_number(max, Infinity),
    p: as_number(p),
  };
};

/**
 * Best-fit band for a base amount. The band whose range contains the amount
 * wins (highest `min` first, so overlapping bands keep the legacy "last tier
 * wins" behaviour). When no range contains it — it sits in a gap between two
 * bands, below every band, or past the last one — the band above takes over,
 * falling back to the highest band when there is nothing above. Every amount,
 * negative included, therefore resolves to a real band.
 *
 * Tiers may be listed in any order and carry negative ranges. Min-only tiers
 * are open-ended, so they behave exactly like the plain `min` thresholds the
 * backend applies — gaps only exist once a tier declares a `max`.
 * @param {Array<{min?: number|string, max?: number|string, p?: number|string}>} markups
 * @param {number|string} base Band base amount
 * @returns {{min: number, max: number, p: number}|null} `null` with no tiers configured
 */
export function select_markup_band(markups, base) {
  const bands = (markups || []).map(to_band).sort((a, b) => a.min - b.min);

  if (!bands.length) {
    return null;
  }

  const amount = as_number(base);
  let best_fit = null;

  bands.forEach((band) => {
    if (amount >= band.min && amount <= band.max) {
      best_fit = band;
    }
  });

  if (!best_fit) {
    best_fit = bands[bands.length - 1];
    const band_above = bands.find((band) => band.min > amount);
    if (band_above) {
      best_fit = band_above;
    }
  }

  return best_fit;
}

/**
 * Compute markup figures (percent, amount, per-pax breakdown) for a
 * flight offer using the source-tier table from `$config.mu` (or the
 * forced override when present).
 * @param {object} flight_offer
 * @param {string} [forced_mu] Forced markup percentage override
 * @returns {object}
 */
export function get_markup(flight_offer, forced_mu) {
  const { source = "DEFAULT", price } = flight_offer;
  let mu = JSON.parse(atob(Vue.prototype.$config.mu));
  let markups = mu[source] ? mu[source] : mu["DEFAULT"];
  let d = {
    markup_base: as_number(price._markup_base),
    grandTotal: number(price.grandTotal || price.total),
    grand_total_mu: 0,
    grand_total_with_mu: 0,
    source: source,
    source_markup: 0,
    all_markup: number(mu.ADD.p),
    mu_percent: number(mu.ADD.p),
    mu_amount: 0,
    mu_price: 0,
    mu_price_medium_per_pax: 0,
    mu_per_pax: 0,
    traveler_pricing: [],
    mu_per_pax_obj: [],
    additional_services_total: 0,
    additional_services_mu_amount: 0,
    additional_services_with_mu: 0,
    tot_fees: 0,
  };

  const band = select_markup_band(markups, d.markup_base);
  if (band) {
    d.source_markup = band.p;
  } else {
    // no band at all means the source has no tier table configured: the offer
    // ships with the integration commission only, so surface it for analytics
    log("get_markup: no markup band configured", source, d.markup_base, mu);
    track_form_event({
      event: "markup:band:missing",
      data: {
        type: "error",
        reason: "markup_band_missing",
        markup_base: d.markup_base,
      },
      context: {
        source,
        flightOffer: slim_flight_offer(flight_offer),
        markups: mu,
      },
    });
  }

  if (forced_mu) {
    // caso CONFIRM -> markup forzato da confirm()
    d.mu_percent = round(
      number(JSON.parse(atob(forced_mu))),
      3,
    );
  } else {
    d.mu_percent = round(sum(d.source_markup, d.all_markup), 3);
  }

  // per gestire il markup dei bagagli nell'estrattoconto
  // occore considerare il total e mu dei servizi aggiunti
  // e sottrarli dal conteggio "tasse" (nostro mu per pax)
  // in quando una volta aggiunti sono considerati nel grandTotal ma hanno voci a parte
  if (flight_offer.price.additionalServices) {
    d.additional_services_total = flight_offer.price.additionalServices.reduce(
      (carry, item) => {
        // kept amount for legacy pre-providers flight offer that might be around
        const { price = {}, amount } = item || {};
        const service_total = price.total || price.grandTotal || amount;
        if (service_total) {
          return sum(carry, number(service_total));
        }
        return carry;
      },
      d.additional_services_total,
    );
  }

  if (d.additional_services_total)
    d.additional_services_mu_amount = round(
      multiply(d.additional_services_total, d.mu_percent),
      decimals,
    );

  d.additional_services_with_mu =
    d.additional_services_total + d.additional_services_mu_amount;

  d.grand_total_mu = round(multiply(d.grandTotal, d.mu_percent), decimals);
  d.grand_total_with_mu = round(sum(d.grandTotal, d.grand_total_mu), decimals);
  d.mu_amount = round(d.grand_total_mu, decimals).toString();
  d.mu_price = round(d.grand_total_with_mu, decimals).toString();
  d.mu_price_medium_per_pax = d.mu_price;
  d.mu_per_pax = subtract(d.mu_amount, d.additional_services_mu_amount);

  if (flight_offer.travelerPricings) {
    const traveler_count = flight_offer.travelerPricings.length;

    d.mu_price_medium_per_pax = round(
      divide(d.grand_total_with_mu, traveler_count),
      decimals,
    ).toString();
    flight_offer.travelerPricings.forEach((traveler) => {
      let new_traveler = deep_clone(traveler);
      new_traveler.price.base = traveler.price.base
        ? round(
            sum(
              number(traveler.price.base),
              multiply(number(traveler.price.base), d.mu_percent),
            ),
            decimals,
          ).toString()
        : 0;
      new_traveler.price.total = round(
        sum(
          number(traveler.price.total),
          multiply(number(traveler.price.total), d.mu_percent),
        ),
        decimals,
      ).toString();
      if (traveler.price.taxes) {
        traveler.price.taxes.forEach((tax, t) => {
          new_traveler.price.taxes[t].amount = round(
            sum(number(tax.amount), multiply(number(tax.amount), d.mu_percent)),
            decimals,
          ).toString();
        });
      }
      d.traveler_pricing.push(new_traveler);
    });

    d.mu_per_pax = divide(
      subtract(d.mu_amount, d.additional_services_mu_amount),
      flight_offer.travelerPricings.length,
    );
  }

  if (flight_offer.price.fees) {
    d.tot_fees = flight_offer.price.fees.reduce((carry, item) => {
      if (item.amount) carry = sum(carry, number(item.amount));
      return carry;
    }, d.tot_fees);
  }

  if (d.tot_fees > 0) {
    d.mu_per_pax = sum(
      d.mu_per_pax,
      divide(d.tot_fees, flight_offer.travelerPricings.length),
    );
  }

  d.mu_per_pax = round(d.mu_per_pax, decimals);

  // creo un oggetto con markup specifico per pax: li sommo e sottraggo la
  // differenza all'ultimo per risolvere problemi di arrotondamenti
  for (let i = 0; i < flight_offer.travelerPricings.length; i++) {
    d.mu_per_pax_obj[i] = d.mu_per_pax;
  }

  try {
    d.mu_per_pax_obj[flight_offer.travelerPricings.length - 1] = round(
      sum(
        d.mu_per_pax_obj[flight_offer.travelerPricings.length - 1],
        subtract(
          subtract(
            sum(d.mu_amount, d.tot_fees),
            d.additional_services_mu_amount,
          ),
          multiply(d.mu_per_pax, flight_offer.travelerPricings.length),
        ),
      ),
      decimals,
    );
  } catch (e) {
    log("get_markup rounding adjustment failed", e);
  }

  return {
    mu_price: d.mu_price,
    mu_amount: d.mu_amount,
    mu_percent: d.mu_percent,
    mu_per_pax: d.mu_per_pax,
    mu_price_medium_per_pax: d.mu_price_medium_per_pax,
    mu_per_pax_obj: d.mu_per_pax_obj,
    traveler_pricing: d.traveler_pricing,
    additional_services_total: d.additional_services_total,
    additional_services_mu_amount: d.additional_services_mu_amount,
    additional_services_with_mu: d.additional_services_with_mu,
  };
}

/**
 * Sum every per-traveler tax line, returning both the raw amount and
 * the marked-up equivalent.
 * @param {object} flight_offer
 * @returns {{amount: number, amount_with_markup: string}}
 */
export function get_taxes_amount(flight_offer) {
  let amount = 0;

  flight_offer.travelerPricings.forEach((traveler) => {
    if (traveler.price.taxes) {
      traveler.price.taxes.forEach((tax) => {
        amount = sum(amount, parseFloat(tax.amount));
      });
    }
  });

  amount = round(amount, decimals);

  return {
    amount: amount,
    amount_with_markup: apply_markup_to_number(flight_offer, amount),
  };
}
